import * as React from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useSubscription } from '@/features/subscriptions/api/get-subscription'
import { useParams } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'

export default function SubscriptionDetails() {
    const subscriptionId = useParams().id || ''
    const { data, isLoading, error } = useSubscription({ subscriptionId })

    if (isLoading) return <div>Loading subscription…</div>
    if (error || !data) return <div className='text-destructive'>Failed to load subscription.</div>

    // Normalize expected shape
    const sub = (data as any).subscription || data
    const items = (data as any).items || sub?.items || []
    const account = sub?.account || (data as any).account
    const store = sub?.store || (data as any).store

    const date = (v?: string | null) => (v ? new Date(v).toLocaleString() : '—')

    return (
        <div className='grid gap-4 md:grid-cols-2'>
            <Card>
                <CardHeader>
                    <div className='flex items-center justify-between'>
                        <CardTitle>Subscription</CardTitle>
                        <div className='flex items-center gap-2'>
                            <Badge variant='outline' className='capitalize'>{sub?.scope || 'account'}</Badge>
                            <Badge variant={sub?.status === 'active' ? 'secondary' : 'outline'} className='capitalize'>{sub?.status || '—'}</Badge>
                            {sub?.primaryFlag ? <Badge>Primary</Badge> : null}
                        </div>
                    </div>
                    <CardDescription>ID: {sub?.subscriptionId}</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                        <div>
                            <div className='text-xs text-muted-foreground'>Account</div>
                            <div className='font-medium truncate'>{account?.name ?? '—'}</div>
                            <div className='text-xs text-muted-foreground truncate'>{account?.email ?? '—'}</div>
                        </div>
                        <div>
                            <div className='text-xs text-muted-foreground'>Source</div>
                            <div className='font-medium capitalize'>{sub?.source ?? '—'}</div>
                        </div>
                        <div>
                            <div className='text-xs text-muted-foreground'>ChargeBee Subscription</div>
                            <div className='font-mono text-sm'>{sub?.chargebeeSubscriptionId ?? '—'}</div>
                        </div>
                        <div>
                            <div className='text-xs text-muted-foreground'>Partner</div>
                            <div className='font-medium'>{sub?.partnerId ?? '—'}</div>
                        </div>
                    </div>

                    <Separator className='my-4' />

                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                        <div>
                            <div className='text-xs text-muted-foreground'>Start</div>
                            <div className='font-medium'>{date(sub?.startAt)}</div>
                        </div>
                        <div>
                            <div className='text-xs text-muted-foreground'>Current Period</div>
                            <div className='font-medium'>{date(sub?.currentPeriodStart)} → {date(sub?.currentPeriodEnd)}</div>
                        </div>
                        <div>
                            <div className='text-xs text-muted-foreground'>Renews</div>
                            <div className='font-medium'>{date(sub?.renewalAt)}</div>
                        </div>
                        <div>
                            <div className='text-xs text-muted-foreground'>Trial Ends</div>
                            <div className='font-medium'>{date(sub?.trialEndAt)}</div>
                        </div>
                        <div>
                            <div className='text-xs text-muted-foreground'>Cancel At</div>
                            <div className='font-medium'>{date(sub?.cancelAt)}</div>
                        </div>
                        <div>
                            <div className='text-xs text-muted-foreground'>Canceled</div>
                            <div className='font-medium'>{date(sub?.canceledAt)}</div>
                        </div>
                    </div>

                    <Separator className='my-4' />
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                        <div>
                            <div className='text-xs text-muted-foreground'>Created</div>
                            <div className='font-medium'>{date(sub?.createdAt)}</div>
                        </div>
                        <div>
                            <div className='text-xs text-muted-foreground'>Updated</div>
                            <div className='font-medium'>{date(sub?.updatedAt)}</div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Line Items</CardTitle>
                    <CardDescription>Plans and add-ons included in this subscription</CardDescription>
                </CardHeader>
                <CardContent>
                    {items.length === 0 ? (
                        <div className='text-muted-foreground'>No items.</div>
                    ) : (
                        <div className='divide-y rounded-md border'>
                            {items.map((it: any) => (
                                <div key={it.subscriptionItemId} className='flex items-start justify-between gap-3 p-3'>
                                    <div className='min-w-0'>
                                        <div className='font-medium'>{it.plan?.name ?? it.planId}</div>
                                        <div className='mt-1 flex flex-wrap items-center gap-2'>
                                            {it.isBasePlan ? <Badge>Base</Badge> : <Badge variant='outline'>Add-on</Badge>}
                                            {it.metered ? <Badge variant='secondary'>Metered</Badge> : <Badge variant='outline'>Flat</Badge>}
                                        </div>
                                        <div className='text-xs text-muted-foreground mt-1 truncate'>SKU: {it.plan?.planSku ?? '—'}</div>
                                    </div>
                                    <div className='text-right'>
                                        <div className='text-xs text-muted-foreground'>Qty</div>
                                        <div className='font-medium'>{it.quantity ?? 1}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            <Card className='md:col-span-2'>
                <CardHeader>
                    <CardTitle>{sub?.scope === 'store' ? 'Store' : 'Account'}</CardTitle>
                </CardHeader>
                <CardContent>
                    {sub?.scope === 'store' && store ? (
                        <div className='grid grid-cols-1 sm:grid-cols-3 gap-3'>
                            <div>
                                <div className='text-xs text-muted-foreground'>Store ID</div>
                                <div className='font-mono text-sm'>{store?.storeId ?? '—'}</div>
                            </div>
                            <div>
                                <div className='text-xs text-muted-foreground'>Name</div>
                                <div className='font-medium'>{store?.name ?? '—'}</div>
                            </div>
                            <div>
                                <div className='text-xs text-muted-foreground'>Email</div>
                                <div className='font-medium'>{store?.email ?? '—'}</div>
                            </div>
                            <div>
                                <div className='text-xs text-muted-foreground'>Phone</div>
                                <div className='font-medium'>{store?.phone ?? '—'}</div>
                            </div>
                            <div>
                                <div className='text-xs text-muted-foreground'>Location</div>
                                <div className='font-medium'>{[store?.city, store?.state].filter(Boolean).join(', ') || '—'}</div>
                            </div>
                            <div>
                                <div className='text-xs text-muted-foreground'>Zip Code</div>
                                <div className='font-medium'>{store?.zipCode ?? '—'}</div>
                            </div>
                            <div>
                                <div className='text-xs text-muted-foreground'>Country</div>
                                <div className='font-medium'>{store?.country ?? '—'}</div>
                            </div>
                            <div className='sm:col-span-3'>
                                <div className='text-xs text-muted-foreground'>Address</div>
                                <div className='font-medium'>{[store?.address1, store?.address2].filter(Boolean).join(' ') || '—'}</div>
                            </div>
                        </div>
                    ) : (
                        <div className='grid grid-cols-1 sm:grid-cols-3 gap-3'>
                            <div>
                                <div className='text-xs text-muted-foreground'>Account ID</div>
                                <div className='font-mono text-sm'>{account?.accountId ?? '—'}</div>
                            </div>
                            <div>
                                <div className='text-xs text-muted-foreground'>Name</div>
                                <div className='font-medium'>{account?.name ?? '—'}</div>
                            </div>
                            <div>
                                <div className='text-xs text-muted-foreground'>Email</div>
                                <div className='font-medium'>{account?.email ?? '—'}</div>
                            </div>
                            <div>
                                <div className='text-xs text-muted-foreground'>Phone</div>
                                <div className='font-medium'>{account?.phone ?? '—'}</div>
                            </div>
                            <div>
                                <div className='text-xs text-muted-foreground'>Location</div>
                                <div className='font-medium'>{[account?.city, account?.state].filter(Boolean).join(', ') || '—'}</div>
                            </div>
                            <div className='sm:col-span-3'>
                                <div className='text-xs text-muted-foreground'>Address</div>
                                <div className='font-medium'>{[account?.address1, account?.address2].filter(Boolean).join(' ') || '—'}</div>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}


